// Bridges the MobX CopilotController to assistant-ui via ExternalStoreRuntime.
// The provider observes `controller.messages`, `controller.busy`, and
// `controller._pendingConfirms`, derives a ThreadMessageLike[] on each render,
// and forwards user input to `controller.ask`.
//
// onCancel is wired to controller.cancel(). assistant-ui's external runtime
// flips composer.canCancel to true when isRunning is true, which makes the
// Composer's <Cancel> primitive active mid-turn; clicking it lands here and
// the controller aborts the in-flight /turn fetch.

import React from "react";
import { observer } from "mobx-react";
import { useExternalStoreRuntime, AssistantRuntimeProvider } from "@assistant-ui/react";
import { convertMessage } from "./convertMessage";
import { CopilotControllerContext, useCopilotController } from "./CopilotControllerContext";

// Re-export so existing imports of useCopilotController / the context
// from this module keep working without a churn-y rename.
export { CopilotControllerContext, useCopilotController };

export const CopilotRuntimeProvider = observer(function CopilotRuntimeProvider({
  controller,
  children,
}) {
  // useExternalStoreRuntime requires the adapter shape `{ messages, convertMessage }`,
  // not pre-converted messages. The runtime feeds raw entries through
  // `convertMessage` and then materializes them via `fromThreadMessageLike`,
  // which fills in id/status/timing fields that a ThreadMessageLike alone
  // lacks. Passing already-converted messages without `convertMessage` makes
  // the runtime treat them as final ThreadMessage[] and they silently fail
  // to render. Capture pendingConfirmIds inside the closure so toggling a
  // safety confirmation rerenders this component and produces a new
  // convertMessage fn, invalidating the runtime's converter cache.
  const pendingConfirmIds = new Set(controller._pendingConfirms.keys());

  // mobx-react's `observer` only re-renders when observables that were *read
  // during render* change. Reading `controller.messages` as a reference does
  // not subscribe us to its contents — pushes during the turn loop would
  // bypass us and the whole conversation would land at once when busy flips
  // back to false. Iterate the array (subscribes to length + each index) and
  // touch every message's mutable fields (subscribes to per-message updates,
  // so `actionResults` assignments mid-loop also flow through). Returns the
  // same observable message objects in a fresh plain-array reference per
  // render, which combined with the new convertMessage closure invalidates
  // the runtime's converter cache cleanly.
  const messages = controller.messages.map((m) => {
    void m.text;
    void m.actions;
    void m.actionResults;
    return m;
  });

  const runtime = useExternalStoreRuntime({
    messages,
    isRunning: controller.busy,
    convertMessage: (m) => convertMessage(m, { pendingConfirmIds }),
    onNew: async (message) => {
      const first = message.content?.[0];
      if (!first || first.type !== "text") return;
      await controller.ask(first.text);
    },
    onCancel: () => {
      controller.cancel();
    },
    // Deliberately not provided:
    //   onEdit, onReload — not coherent for a screen-control agent.
  });

  return (
    <CopilotControllerContext.Provider value={controller}>
      <AssistantRuntimeProvider runtime={runtime}>
        {children}
      </AssistantRuntimeProvider>
    </CopilotControllerContext.Provider>
  );
});
